import { supabase } from "@/lib/supabase";
import type {
  SellerApplication,
  VerificationDoc,
} from "@/lib/seller-application";

export interface ReviewApplication {
  application: SellerApplication;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  seller_status: string;
  docs: VerificationDoc[];
}

export async function fetchReviewApplications(): Promise<ReviewApplication[]> {
  const [appsRes, profilesRes, docsRes] = await Promise.all([
    supabase
      .from("seller_applications")
      .select("*")
      .order("created_at", { ascending: false }),
    supabase
      .from("profiles")
      .select("id, full_name, email, avatar_url, seller_status, created_at"),
    supabase
      .from("seller_verification_documents")
      .select("id, user_id, document_type, storage_path, status"),
  ]);

  if (appsRes.error) throw appsRes.error;
  if (profilesRes.error) throw profilesRes.error;
  if (docsRes.error) throw docsRes.error;

  const profilesById = new Map(
    (profilesRes.data ?? []).map((p) => [p.id, p])
  );
  const docsByUser = new Map<string, VerificationDoc[]>();
  for (const d of docsRes.data ?? []) {
    const list = docsByUser.get(d.user_id) ?? [];
    list.push(d as VerificationDoc);
    docsByUser.set(d.user_id, list);
  }

  return (appsRes.data ?? []).map((app) => {
    const a = app as SellerApplication;
    const p = profilesById.get(a.user_id);
    return {
      application: a,
      full_name: p?.full_name ?? null,
      email: p?.email ?? null,
      avatar_url: p?.avatar_url ?? null,
      seller_status: p?.seller_status ?? a.status,
      docs: docsByUser.get(a.user_id) ?? [],
    };
  });
}

export async function getSignedDocUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("seller-verification")
    .createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

function sendSellerNotification(
  userId: string,
  code: string,
  variables: Record<string, string> = {}
): void {
  supabase.functions
    .invoke("send-email", {
      body: {
        template_code: code,
        target_user_id: userId,
        variables,
        // Clave estable: no reenvía el mismo evento para el mismo usuario.
        event_key: `${code}:${userId}`,
      },
    })
    .catch(() => {
      // Silencioso: el envío de email es no bloqueante.
    });
}

export async function approveSeller(userId: string): Promise<void> {
  const { error } = await supabase.rpc("approve_seller", { user_uuid: userId });
  if (error) throw new Error(error.message);
  sendSellerNotification(userId, "SELLER_APPROVED");
}

export async function rejectSeller(
  userId: string,
  reason: string
): Promise<void> {
  const { error } = await supabase.rpc("reject_seller", {
    user_uuid: userId,
    reason,
  });
  if (error) throw new Error(error.message);
  sendSellerNotification(userId, "SELLER_REJECTED", { rejection_reason: reason });
}

export async function suspendSeller(
  userId: string,
  reason: string
): Promise<void> {
  const { error } = await supabase.rpc("suspend_seller", {
    user_uuid: userId,
    reason,
  });
  if (error) throw new Error(error.message);
  sendSellerNotification(userId, "SELLER_SUSPENDED", { suspension_reason: reason });
}